import { FormControl, FormErrorMessage } from '@chakra-ui/react';
import PluginEditor from '@draft-js-plugins/editor';
import { EditorState } from 'draft-js';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { TextEditor } from '@/imports/ui/components/TextEditor';
import { getContentWithMention } from '@/imports/ui/components/TextEditor/utils';
import {
  PlatformContextPreviewLink,
  useLinkPreview,
} from '@/imports/ui/components/context/PlatformContextPreviewLink';

export const Body = React.forwardRef<
  PluginEditor,
  {
    validationMessage: string;
    placeholder: string;
    mentionData?: { _id: string; postId: string; displayName: string };
  }
>(({ validationMessage, placeholder, mentionData }, ref) => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const [currentMentionData, setCurrentMentionData] =
    React.useState(mentionData);

  const bodyEditorState = watch('body');

  const onHidePreviewHandler = async (
    editorState: EditorState
  ): Promise<void> => {
    setValue('body', editorState);
  };

  const { linkPreview, hidePreview } = useLinkPreview({
    editorState: bodyEditorState,
    onHidePreviewHandler,
  });

  React.useEffect(() => {
    register('body', {
      validate: (value) =>
        !!value.getCurrentContent().getPlainText() &&
        value.getCurrentContent().getPlainText().length > 0
          ? true
          : validationMessage,
    });
  }, [register, validationMessage]);

  React.useEffect(() => {
    if (currentMentionData && bodyEditorState) {
      const content = bodyEditorState.getCurrentContent();
      if (content.getPlainText().length === 0) {
        const mentionContent = getContentWithMention({
          _id: currentMentionData._id,
          postId: currentMentionData.postId,
          displayName: currentMentionData.displayName,
        });
        setValue(
          'body',
          EditorState.moveFocusToEnd(
            EditorState.createWithContent(mentionContent)
          )
        );
      }
    }
  }, [bodyEditorState, currentMentionData, setValue]);

  return (
    <>
      <FormControl isInvalid={!!errors.body?.message}>
        <TextEditor
          ariaLabel="create comment body"
          placeholder={placeholder}
          isMentionsActive
          ref={ref}
          value={bodyEditorState}
          onChange={(val) => {
            setValue('body', val);
            if (
              val.getCurrentContent().getPlainText().length === 0 &&
              currentMentionData
            ) {
              setCurrentMentionData(undefined);
            }
          }}
          backgroundColor="gray.50"
          borderRadius="2xl"
          borderColor="transparent"
          boxShadow="none"
        />
        <FormErrorMessage>{errors.body?.message}</FormErrorMessage>
      </FormControl>
      <PlatformContextPreviewLink
        linkPreview={linkPreview}
        canEdit
        onClose={hidePreview}
        w="full"
      />
    </>
  );
});

Body.displayName = 'Body';
